import React, { StrictMode } from "react";

import SignIn from "./authentication/signin/signin.jsx";
import ForgotPassword from "./authentication/forgotPassword/forgotPassword.jsx";
import ChangePassword from "./authentication/changePassword/changePassword.jsx";
import { AuthProvider } from "./context/authContext.jsx";
import RoleBasedRedirect from "./routing/roleBasedRedirect.jsx";
import ProtectedRoute from "./routing/protectedRoute.jsx";
import RouteErrorElement from "./routing/appErrorBoundary/routeErrorElement.jsx";
import AppErrorBoundary from "./routing/appErrorBoundary/appErrorBoundary.jsx";
import AppLayout from "./layouts/appLayout/appLayout.jsx";
import { ROLES } from "./constants/roles.js";

// Admin / Manager workspace pages
import AdminDashboard from "./adminWorkspace/dashboard/dashboard.jsx";
import AdminVentures from "./adminWorkspace/ventures/ventures.jsx";
import AddVentures from "./adminWorkspace/ventures/addVentures.jsx";
import VentureDetail from "./adminWorkspace/ventures/ventureDetail.jsx";
import AdminImpact from "./adminWorkspace/impact/impact.jsx";
import AdminPortfolio from "./adminWorkspace/portfolio/portfolio.jsx";
import AdminDataCollection from "./adminWorkspace/dataCollection/dataCollection.jsx";
import AdminReports from "./adminWorkspace/reports/reports.jsx";
import AdminUsers from "./adminWorkspace/users/users.jsx";
import AdminSettings from "./adminWorkspace/settings/settings.jsx";
import AdminTraining from "./adminWorkspace/training/training.jsx";

// Funder workspace pages
import FunderDashboard from "./funderWorkspace/dashboard/dashboard.jsx";
import FunderImpact from "./funderWorkspace/impact/impact.jsx";
import FunderFinancial from "./funderWorkspace/financial/financial.jsx";
import FunderPrograms from "./funderWorkspace/programs/programs.jsx";
import FunderReports from "./funderWorkspace/reports/reports.jsx";

// Venture workspace pages
import VentureDashboard from "./ventureWorkspace/dashboard/dashboard.jsx";
import VentureSubmit from "./ventureWorkspace/submit/submit.jsx";
import VentureSupport from "./ventureWorkspace/support/support.jsx";

import ReactDOM from "react-dom/client";
import * as Sentry from "@sentry/react";
import {
  QueryClient,
  QueryClientProvider,
  QueryCache,
} from "@tanstack/react-query";
import {
  createBrowserRouter,
  RouterProvider,
  useLocation,
  useNavigationType,
  createRoutesFromChildren,
  matchRoutes,
} from "react-router-dom";
import "./index.css";

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  environment: import.meta.env.MODE,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.reactRouterV7BrowserTracingIntegration({
      useEffect: React.useEffect,
      useLocation,
      useNavigationType,
      createRoutesFromChildren,
      matchRoutes,
    }),
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: 1.0,
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,
});

const sentryCreateBrowserRouter =
  Sentry.wrapCreateBrowserRouterV7(createBrowserRouter);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
  queryCache: new QueryCache({
    onError: (error) => Sentry.captureException(error),
  }),
});

const router = sentryCreateBrowserRouter([
  { path: "/signin", element: <SignIn />, errorElement: <RouteErrorElement /> },
  {
    path: "/forgot-password",
    element: <ForgotPassword />,
    errorElement: <RouteErrorElement />,
  },
  {
    path: "/reset-password",
    element: <ChangePassword />,
    errorElement: <RouteErrorElement />,
  },
  {
    path: "/",
    element: <RoleBasedRedirect />,
    errorElement: <RouteErrorElement />,
  },

  // Admin workspace
  {
    element: (
      <ProtectedRoute allowedRoles={[ROLES.ADMIN]}>
        <AppLayout />
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorElement />,
    children: [
      { path: "/admin/dashboard", element: <AdminDashboard /> },
      { path: "/admin/ventures", element: <AdminVentures /> },
      { path: "/admin/ventures/new", element: <AddVentures /> },
      { path: "/admin/ventures/:id", element: <VentureDetail /> },
      { path: "/admin/impact", element: <AdminImpact /> },
      { path: "/admin/portfolio", element: <AdminPortfolio /> },
      { path: "/admin/data-collection", element: <AdminDataCollection /> },
      { path: "/admin/reports", element: <AdminReports /> },
      { path: "/admin/training", element: <AdminTraining /> },
      { path: "/admin/users", element: <AdminUsers /> },
      { path: "/admin/settings", element: <AdminSettings /> },
    ],
  },

  // Manager workspace
  {
    element: (
      <ProtectedRoute allowedRoles={[ROLES.MANAGER]}>
        <AppLayout />
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorElement />,
    children: [
      { path: "/manager/dashboard", element: <AdminDashboard /> },
      { path: "/manager/ventures", element: <AdminVentures /> },
      { path: "/manager/ventures/new", element: <AddVentures /> },
      { path: "/manager/ventures/:id", element: <VentureDetail /> },
      { path: "/manager/impact", element: <AdminImpact /> },
      { path: "/manager/portfolio", element: <AdminPortfolio /> },
      { path: "/manager/data-collection", element: <AdminDataCollection /> },
      { path: "/manager/reports", element: <AdminReports /> },
      { path: "/manager/training", element: <AdminTraining /> },
    ],
  },

  // Funder workspace
  {
    element: (
      <ProtectedRoute allowedRoles={[ROLES.FUNDER]}>
        <AppLayout />
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorElement />,
    children: [
      { path: "/funder/dashboard", element: <FunderDashboard /> },
      { path: "/funder/impact", element: <FunderImpact /> },
      { path: "/funder/financial", element: <FunderFinancial /> },
      { path: "/funder/programs", element: <FunderPrograms /> },
      { path: "/funder/reports", element: <FunderReports /> },
    ],
  },

  // Venture workspace
  {
    element: (
      <ProtectedRoute allowedRoles={[ROLES.VENTURE]}>
        <AppLayout />
      </ProtectedRoute>
    ),
    errorElement: <RouteErrorElement />,
    children: [
      { path: "/venture/dashboard", element: <VentureDashboard /> },
      { path: "/venture/support", element: <VentureSupport /> },
    ],
  },

  // Catch-all → send to root, which will re-route by role/auth.
  {
    path: "*",
    element: <RoleBasedRedirect />,
    errorElement: <RouteErrorElement />,
  },
]);

ReactDOM.createRoot(document.getElementById("root")).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <AppErrorBoundary>
        {/* AuthProvider wraps RouterProvider so every route can call useAuth(). */}
        <AuthProvider>
          <RouterProvider router={router} />
        </AuthProvider>
      </AppErrorBoundary>
    </QueryClientProvider>
  </StrictMode>,
);
